import {deleteCookie, getCookie, setCookie} from 'vinxi/http'
import {notFound} from '@tanstack/react-router'
import {createServerFn} from '@tanstack/start'
import {z} from 'zod'
import createCouponForPromotion from '~/client/spreadshop/old/createCouponForPromotion'
import updatedBasketItem from '~/client/spreadshop/old/updateBasketItem'
import addBasketItem from '~/client/spreadshop/old/addBasketItem'
import createBasket from '~/client/spreadshop/old/createBasket'
import fetchBasket from '~/client/spreadshop/old/fetchBasket'
import addCoupon from '~/client/spreadshop/old/addCoupon'
import {Locale} from '~/gql/graphql'

const basketParams = z.object({
    locale: z.nativeEnum(Locale).optional(),
})

const BASKET_MAX_AGE = 60 * 60 * 24 * 20 // 20 days

export const getBasket = createServerFn({
    method: 'GET',
})
    .validator((data: unknown) => {
        const result = basketParams.safeParse(data)
        if (result.error) {
            throw notFound()
        }
        return result.data
    })
    .handler(async ({data}) => {
        const {locale} = data
        try {
            const basketId = getCookie('basket')
            if (!basketId?.length) {
                return null
            }

            return fetchBasket(basketId, locale)
        } catch (e) {
            deleteCookie('basket')
            return null
        }
    })

const addProductToBasketParams = z.object({
    sku: z.string(),
    sizeId: z.string(),
    appearanceId: z.string(),
    locale: z.nativeEnum(Locale).optional(),
})

export const addProductToBasket = createServerFn({
    method: 'POST',
})
    .validator((data: unknown) => {
        const result = addProductToBasketParams.safeParse(data)
        if (result.error) {
            throw notFound()
        }

        return result.data
    })
    .handler(async ({data}) => {
        const {sku, sizeId, appearanceId, locale} = data
        const basketId = getCookie('basket')

        const basket = !basketId
            ? await createBasket(sku, sizeId, appearanceId, 1, locale)
            : await addBasketItem(basketId, sku, sizeId, appearanceId, 1, locale)

        setCookie('basket', basket.id, {httpOnly: true, maxAge: BASKET_MAX_AGE})

        const promotionCode = getCookie('promotionCode')
        if (!promotionCode) {
            return
        }

        try {
            await addCoupon(basket.id, promotionCode)
            deleteCookie('promotionCode')
        } catch (e) {}
    })

const updateQuantityParams = z.object({
    productId: z.string(),
    sizeId: z.string(),
    appearanceId: z.string(),
    quantity: z.number().min(0).max(999),
})

export const updateQuantity = createServerFn({
    method: 'POST',
})
    .validator((data: unknown) => {
        const result = updateQuantityParams.safeParse(data)
        if (result.error) {
            throw notFound()
        }

        return result.data
    })
    .handler(async ({data}) => {
        const {productId, sizeId, appearanceId, quantity} = data
        const basketId = getCookie('basket')

        if (!basketId) {
            return
        }

        await updatedBasketItem(basketId, productId, sizeId, appearanceId, quantity)

        const promotionCode = getCookie('promotionCode')
        if (!promotionCode) {
            return
        }

        try {
            await addCoupon(basketId, promotionCode)
            deleteCookie('promotionCode')
        } catch (e) {}
    })

const applyCouponParams = z.object({
    code: z.string().optional(),
})

export const applyCoupon = createServerFn({
    method: 'POST',
})
    .validator((data: unknown) => {
        const result = applyCouponParams.safeParse(data)
        if (result.error) {
            throw notFound()
        }

        return result.data
    })
    .handler(async ({data}) => {
        deleteCookie('promotionCode')
        const {code} = data

        const promotionCode = code ?? (await createCouponForPromotion()).couponCode

        const basketId = getCookie('basket')

        if (basketId) {
            try {
                await addCoupon(basketId, promotionCode)
            } catch (e) {
                setCookie('promotionCode', promotionCode, {
                    httpOnly: true,
                    maxAge: 60 * 60 * 24,
                }) // 1d
            }
        } else {
            setCookie('promotionCode', promotionCode, {httpOnly: true, maxAge: 60 * 60 * 24}) // 1d
        }
    })
